import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { hasAskedHere, rememberAskedHere } from '../../push/askedHere';
import { browserEnvironment, requestNotificationPermission } from '../../push/browser';
import { canAskForPush } from '../../push/device';
import type { RealtimeChannel } from '../../types/domain.types';
import { mySettingsQueryOptions } from '../../utils/queryOptions';
import { useUpdateMySettings } from '../mutations/useUpdateMySettings';

import { useTurnOnThisDevice } from './useTurnOnThisDevice';

export type AlertsStillCome = RealtimeChannel | 'APP';

export interface PushPermissionAsk {
  readonly open: boolean;
  readonly stillComes: AlertsStillCome;
  readonly allow: () => void;
  readonly notNow: () => void;
}

export const usePushPermissionAsk = (): PushPermissionAsk => {
  const settings = useQuery(mySettingsQueryOptions());
  const [askable] = useState(() => canAskForPush(browserEnvironment(), hasAskedHere()));
  const [answered, setAnswered] = useState(false);
  const { mutate: turnOn } = useTurnOnThisDevice();
  const { mutate: recordPrompt } = useUpdateMySettings();
  const mine = settings.data;

  const answer = (): void => {
    const at = new Date().toISOString();
    rememberAskedHere(at);
    setAnswered(true);
    recordPrompt({ patch: { pushPromptedAt: at } });
  };

  return {
    open: askable && !answered && mine !== undefined && mine.push.promptedAt === null,
    stillComes: mine?.reachesOn ?? 'APP',
    allow: () => {
      const asking = requestNotificationPermission();
      answer();
      turnOn(asking);
    },
    notNow: answer,
  };
};
